  import React, { useContext, useEffect, useState } from "react";
  import axios from "axios";
  import { login_url, auth_url, logout_url } from "../utils/constants";
  import { getApiError } from "../utils/helpers";

  axios.defaults.withCredentials = true;

  // Auto-logout on session expiry: any 401 (except a failed login attempt
  // itself) means the token is invalid/expired, so send the admin back to
  // the login screen instead of leaving them on a page that silently fails.
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error.response?.status;
      const isLoginRequest = (error.config?.url || "").includes(login_url);
      if (status === 401 && !isLoginRequest) {
        localStorage.clear();
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }
      return Promise.reject(error);
    }
  );

  const UserContext = React.createContext();

  export const UserProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);

    const setUser = (user) => {
      setCurrentUser(user);
    };

    const checkAuth = async () => {
      try {
        setAuthLoading(true);
        const response = await axios.post(auth_url);
        const { success, data, token } = response.data;
        if (success && data) {
          setUser(data);
          // Restore the token in localStorage so Bearer-auth API calls work
          if (token) {
            localStorage.setItem("acces_token", token);
            localStorage.setItem("access_id", data.id);
            if (["super", "moderate", "low"].includes(data.privilege)) {
              localStorage.setItem("IsAdmin", true);
            } else {
              localStorage.setItem("IsAdmin", false);
            }
          }
        }
        setAuthLoading(false);
      } catch (error) {
        setAuthLoading(false);
      }
    };

    const login = async (email, password) => {
      try {
        const response = await axios.post(login_url, { email, password });
        localStorage.setItem("acces_token", response.data.token);
        localStorage.setItem("access_id", response.data.data.id);
        if (["super", "moderate", "low"].includes(response.data.data.privilege)) {
          localStorage.setItem("IsAdmin", true);
        } else {
          localStorage.setItem("IsAdmin", false);
        }

        const { success, data } = response.data;
        setUser(data);
        return { success, data };
      } catch (error) {
        const { message, success } = getApiError(error);
        return { success, message };
      }
    };

    const logout = async () => {
      try {
        const response = await axios.get(logout_url);
        const { success, message } = response.data;
        setUser(null);
        localStorage.clear()
        return { success, message };
      } catch (error) {
        const { success, message } = getApiError(error);
        return { success, message };
      }
    };

    useEffect(() => {
      checkAuth();
      // eslint-disable-next-line
    }, []);

    return (
      <UserContext.Provider value={{ currentUser, authLoading, login, logout }}>
        {children}
      </UserContext.Provider>
    );
  };

  export const useUserContext = () => {
    return useContext(UserContext);
  };
